import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { AdminGuard, AdminNav } from "../components/admin/AdminShell";
import { imgUrl } from "../lib/format";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { CheckCircle, XCircle, ArrowsClockwise, Warning } from "@phosphor-icons/react";

const STATUS_COLORS = {
  "Pending Review": "border-amber-500/40 text-amber-300 bg-amber-500/5",
  "Approved": "border-emerald-500/40 text-emerald-300 bg-emerald-500/5",
  "Rejected": "border-red-500/40 text-red-300 bg-red-500/5",
  "Needs Replacement": "border-fuchsia-500/40 text-fuchsia-300 bg-fuchsia-500/5",
  "needs_generation": "border-slate-500/40 text-brand-text bg-slate-500/5",
};

const MATCH_COLORS = {
  "Exact Match": "text-emerald-300",
  "Probable Match": "text-brand-accent",
  "Duplicate Image": "text-amber-300",
  "Label Mismatch": "text-red-300",
  "Missing Image": "text-brand-muted",
  "Needs Replacement": "text-fuchsia-300",
};

export default function AdminImageReview() {
  const [report, setReport] = useState(null);
  const [filter, setFilter] = useState("");
  const [reviewFilter, setReviewFilter] = useState("");

  const load = async () => {
    const { data } = await api.get("/admin/image-report");
    setReport(data);
  };

  useEffect(() => { load(); }, []);

  const setReview = async (slug, status) => {
    try {
      await api.post(`/admin/products/${slug}/image-review`, { image_review_status: status });
      toast.success(`Image ${status}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  if (!report) return <AdminGuard><AdminNav /><div className="max-w-6xl mx-auto px-4 py-24 text-brand-muted">Loading…</div></AdminGuard>;

  const s = report.summary || {};
  const products = (report.products || []).filter((p) => {
    if (filter && !p.product_name.toLowerCase().includes(filter.toLowerCase()) && !p.public_sku.toLowerCase().includes(filter.toLowerCase())) return false;
    if (reviewFilter && p.image_review_status !== reviewFilter) return false;
    return true;
  });

  return (
    <AdminGuard>
      <AdminNav />
      <div className="max-w-7xl mx-auto px-4 py-10" data-testid="admin-image-review-page">
        <div className="mb-6">
          <div className="overline mb-2">Product photography</div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-brand-navy">Image Review</h1>
          <p className="mt-2 text-brand-muted text-sm max-w-2xl">
            Products with rejected or unreviewed images may remain visible on the catalog, but only <span className="text-emerald-300 font-mono">Approved</span> images are considered publicly verified. Placeholder is shown for missing / needs-generation entries.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-brand-surface border border-brand-border mb-8">
          {[
            ["Exact matches", s.exact_matches ?? 0, "text-emerald-300"],
            ["Duplicate cells", s.duplicates ?? 0, "text-amber-300"],
            ["Missing / needs generation", s.missing ?? 0, "text-brand-text"],
            ["Total cells processed", s.cells?.length ?? 0, "text-brand-accent"],
          ].map(([k, v, c]) => (
            <div key={k} className="bg-white p-6">
              <div className="overline mb-2">{k}</div>
              <div className={`font-mono text-2xl ${c}`}>{v}</div>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-3 mb-4">
          <input
            placeholder="Filter by product / SKU"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            data-testid="image-review-filter"
            className="w-full max-w-sm bg-white/60 border border-brand-border px-3 py-2 text-sm text-brand-navy"
          />
          <Select value={reviewFilter || "__all"} onValueChange={(v) => setReviewFilter(v === "__all" ? "" : v)}>
            <SelectTrigger className="max-w-[220px]" data-testid="image-review-status-filter"><SelectValue placeholder="Filter by review status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">All statuses</SelectItem>
              <SelectItem value="Pending Review">Pending Review</SelectItem>
              <SelectItem value="Approved">Approved</SelectItem>
              <SelectItem value="Rejected">Rejected</SelectItem>
              <SelectItem value="Needs Replacement">Needs Replacement</SelectItem>
              <SelectItem value="needs_generation">Needs generation</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {products.map((p) => (
            <div key={p.slug} className="border border-brand-border bg-white" data-testid={`image-card-${p.slug}`}>
              <div className="relative aspect-square bg-brand-surface overflow-hidden">
                {p.primary_image_url ? (
                  <img src={imgUrl(p.primary_image_url)} alt={p.image_alt_text} className="w-full h-full object-cover" loading="lazy" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-brand-muted font-mono text-xs">No image</div>
                )}
                <div className="absolute top-2 left-2 text-[9px] font-mono uppercase tracking-wider bg-white/80 border border-brand-border text-brand-text px-1.5 py-0.5">
                  {p.source_grid_cell || "—"}
                </div>
              </div>
              <div className="p-4">
                <div className="font-display text-brand-navy text-sm">{p.product_name}</div>
                <div className="text-xs text-brand-muted font-mono">{p.strength} · {p.public_sku}</div>
                <div className="mt-3 flex items-center justify-between">
                  <span className={`text-[10px] font-mono uppercase tracking-wider ${MATCH_COLORS[p.image_match_status] || "text-brand-muted"}`}>
                    {p.image_match_status || "—"}
                  </span>
                  <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider border ${STATUS_COLORS[p.image_review_status] || "border-brand-border text-brand-muted"}`}>
                    {p.image_review_status || "Pending Review"}
                  </span>
                </div>
                <div className="mt-3 flex items-center gap-2">
                  <button
                    onClick={() => setReview(p.slug, "Approved")}
                    data-testid={`image-approve-${p.slug}`}
                    className="flex-1 inline-flex items-center justify-center gap-1 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 text-[10px] font-mono uppercase tracking-widest py-1.5"
                  >
                    <CheckCircle size={12} weight="bold" /> Approve
                  </button>
                  <button
                    onClick={() => setReview(p.slug, "Rejected")}
                    data-testid={`image-reject-${p.slug}`}
                    className="flex-1 inline-flex items-center justify-center gap-1 border border-red-500/40 text-red-300 hover:bg-red-500/10 text-[10px] font-mono uppercase tracking-widest py-1.5"
                  >
                    <XCircle size={12} weight="bold" /> Reject
                  </button>
                  <button
                    onClick={() => setReview(p.slug, "Needs Replacement")}
                    data-testid={`image-replace-${p.slug}`}
                    className="flex-1 inline-flex items-center justify-center gap-1 border border-fuchsia-500/40 text-fuchsia-300 hover:bg-fuchsia-500/10 text-[10px] font-mono uppercase tracking-widest py-1.5"
                  >
                    <ArrowsClockwise size={12} weight="bold" /> Replace
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </AdminGuard>
  );
}
